import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FcConfirmDialog } from "../modals/classes/schedule/ui";
import * as userActions from "../redux/reducers/userReducer";
import { userState } from "../redux/reducers/userReducer";
import axiosInstance from "../utils/axiosInstance";
import { getUrlForUsers } from "../utils/confighelpers";
import { hasConnectedStripe, STRIPE_SETUP_ROUTE } from "../utils/onboarding";

/**
 * "Set up payments first": members are created as customers in the club's payment account (Stripe), so adding,
 * inviting, importing members and sharing the club / trial link wait until it is connected.
 *
 *   const payments = usePaymentsGate();
 *   onClick={() => payments.whenReady(() => doIt())}
 *   <PaymentsRequiredDialog {...payments.dialog} message="..." />
 *
 * Redux can be out of date right after connecting, so the server is asked once before the dialog is shown.
 * User-facing text says "payments" / "payment account", not "Stripe".
 */
/**
 * Reloads the club's own account data (users/getUserInfo) into Redux, at most once every 30 seconds however many
 * components ask. Used when something reads "payments not set up" / "profile missing" from Redux, which can be an
 * older copy (2026-10-28: the members page showed "Set up payments" while the payment account was ready).
 */
let refreshing = null;
let refreshedAt = 0;
export const refreshUserDataOnce = (dispatch, current = null) => {
  if (refreshing) return refreshing;
  if (Date.now() - refreshedAt < 30 * 1000) return Promise.resolve(null);
  refreshing = axiosInstance({ method: "get", url: `${getUrlForUsers()}getUserInfo` })
    .then((res) => {
      const fresh = res?.data?.statusCode === 200 ? res.data.data : null;
      if (fresh) dispatch(userActions.setUserData({ ...(current?.appUrl ? { appUrl: current.appUrl } : {}), ...fresh }));
      return fresh;
    })
    .catch(() => null)
    .finally(() => {
      refreshedAt = Date.now();
      refreshing = null;
    });
  return refreshing;
};

export function usePaymentsGate() {
  const dispatch = useDispatch();
  const userData = useSelector(userState).userData;
  const [open, setOpen] = React.useState(false);
  const ready = hasConnectedStripe(userData);

  // Not ready according to Redux: check the server once, so the "Set up payments" banner never shows from an old copy
  React.useEffect(() => {
    if (!ready && userData?._id) refreshUserDataOnce(dispatch, userData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, userData?._id]);

  const whenReady = async (action) => {
    if (hasConnectedStripe(userData)) return action();
    try {
      const res = await axiosInstance({ method: "get", url: `${getUrlForUsers()}getUserInfo` });
      const fresh = res?.data?.statusCode === 200 ? res.data.data : null;
      if (fresh) {
        dispatch(userActions.setUserData(fresh));
        if (hasConnectedStripe(fresh)) return action();
      }
    } catch (e) {
      // fall through to the dialog
    }
    setOpen(true);
  };

  return { ready, whenReady, dialog: { open, onClose: () => setOpen(false) } };
}

export const PAYMENTS_REQUIRED_TEXT = {
  members: "Your members buy passes and subscriptions through your club's payment account. Once it's set up, you can add, invite or import members.",
  link: "Your members buy passes and subscriptions through your club's payment account. Once it's set up, you can copy and share your club link.",
};

export function PaymentsRequiredDialog({ open, onClose, message = PAYMENTS_REQUIRED_TEXT.members }) {
  const navigate = useNavigate();
  return (
    <FcConfirmDialog
      open={open}
      title="Set up payments first"
      message={message}
      confirmLabel="Set up payments"
      cancelLabel="Not now"
      onClose={onClose}
      onConfirm={() => {
        onClose();
        navigate(STRIPE_SETUP_ROUTE);
      }}
    />
  );
}
